import type { Industry } from '@/types/exam'
import type { ExamSearchEntry } from '@/data/exams/search-index'

export interface IndustryGroup {
  industry: Industry
  entries: ExamSearchEntry[]
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function isSubsequence(query: string, target: string): boolean {
  if (query.length === 0) return false
  let matched = 0
  for (const char of target) {
    if (char === query[matched]) matched += 1
    if (matched === query.length) return true
  }
  return false
}

function scoreField(query: string, field: string, weight: number): number {
  if (field.length === 0) return 0
  if (field === query) return 100 * weight
  if (field.startsWith(query)) return 80 * weight
  const index = field.indexOf(query)
  if (index > 0) return (60 - Math.min(index, 30)) * weight
  if (isSubsequence(query, field)) return 15 * weight
  return 0
}

export function scoreEntry(entry: ExamSearchEntry, query: string): number {
  const normalized = normalize(query)
  if (normalized.length === 0) return 0
  return Math.max(
    scoreField(normalized, normalize(entry.name), 3),
    scoreField(normalized, normalize(entry.credential), 2.5),
    scoreField(normalized, normalize(entry.full), 2),
    scoreField(normalized, normalize(entry.industry), 1)
  )
}

/**
 * Fuzzy-ranks the index against a query. An empty query returns the index
 * unchanged (capped at `limit`) so callers can render a browse-all state.
 */
export function searchExams(
  entries: ExamSearchEntry[],
  query: string,
  limit: number = 8
): ExamSearchEntry[] {
  if (normalize(query).length === 0) return entries.slice(0, limit)
  return entries
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter((scored) => scored.score > 0)
    .sort(
      (a, b) => b.score - a.score || a.entry.name.localeCompare(b.entry.name)
    )
    .slice(0, limit)
    .map((scored) => scored.entry)
}

/** Groups entries by industry, preserving entry order and first-seen industry order. */
export function groupByIndustry(entries: ExamSearchEntry[]): IndustryGroup[] {
  const groups: IndustryGroup[] = []
  for (const entry of entries) {
    const existing = groups.find((group) => group.industry === entry.industry)
    if (existing) {
      existing.entries.push(entry)
    } else {
      groups.push({ industry: entry.industry, entries: [entry] })
    }
  }
  return groups
}
